import * as React from "react";
import { SnackbarState } from "models/user-models";
import AppSnackbar from "components/snakbar";
import {
  DefaultBtnModelProps,
  defaultBtnObj,
} from "models/wizard/wizard-modals";

const DefaultBtnModel: React.FC<DefaultBtnModelProps> = ({
  desc,
  data,
  isSelected,
  onSubmit,
  isInData,
  onRemove,
  textSize = "medium",
}) => {
  const optionFontSize =
    textSize === "small" ? "14px" : textSize === "large" ? "22px" : "18px";
  const modalTitleFontSize =
    textSize === "small" ? "15px" : textSize === "large" ? "22px" : "18px";
  const modalTextFontSize =
    textSize === "small" ? "13px" : textSize === "large" ? "18px" : "14px";
  const optionBoxHeight =
    textSize === "small" ? "85px" : textSize === "large" ? "125px" : "100px";

  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const [temperatureValue, setTemperatureValue] = React.useState<defaultBtnObj>(
    { inputValue: "", unitValue: "Celsius" }
  );
  const [temperatureDecimalValue, setTemperatureDecimalValue] =
    React.useState<defaultBtnObj>({ inputValue: "", unitValue: "Celsius" });

  const [heightFeet, setHeightFeet] = React.useState<defaultBtnObj>({
    inputValue: "",
    unitValue: "Feets",
  });
  const [heightInches, setHeightInches] = React.useState<defaultBtnObj>({
    inputValue: "",
    unitValue: "Inches",
  });
  const [heightCM, setHeightCM] = React.useState<defaultBtnObj>({
    inputValue: "",
    unitValue: "Centimeters",
  });
  const [weight, setWeight] = React.useState<defaultBtnObj>({
    inputValue: "",
    unitValue: "",
  });

  const [activeTab, setActiveTab] = React.useState(
    desc === "Height & Weight" ? "Imperial" : "Celsius"
  );
  const [inputValue, setInputValue] = React.useState("");
  const [unitValue, setUnitValue] = React.useState(
    desc === "Blood Pressure" ? "mmHg" : desc === "Heart Rate" ? "BPM" : ""
  );
  const [snackbar, setSnackbar] = React.useState<SnackbarState>({
    open: false,
    message: "",
    severity: "info",
  });

  const validateFields = () => {
    if (desc === "Height & Weight") {
      if (activeTab === "Imperial") {
        if (
          !heightFeet?.inputValue ||
          !heightInches?.inputValue ||
          !weight?.inputValue
        ) {
          return false;
        }
      } else if (activeTab === "Metric") {
        if (!heightCM?.inputValue || !weight?.inputValue) {
          return false;
        }
      }
    } else if (desc === "Temperature") {
      if (!temperatureValue?.inputValue) {
        return false;
      }
    } else {
      const currentUnit =
        desc === "Blood Pressure"
          ? "mmHg"
          : desc === "Heart Rate"
            ? "BPM"
            : unitValue;
      if (!inputValue || !currentUnit) {
        return false;
      }
    }
    return true;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let raw = e.target.value;

    // Allow only numbers and slash
    raw = raw.replace(/[^0-9/]/g, "");

    // If multiple slashes exist, keep only the first one
    const slashCount = (raw.match(/\//g) || []).length;
    if (slashCount > 1) {
      raw = raw.replace(/\/(.*)\//, "/$1"); // remove the second slash and beyond
      raw = raw.replace(/\/+/g, "/"); // ensure single slash
    }

    setInputValue(raw);
  };

  const handleRemove = () => {
    onRemove(desc);
    // handleClose();
  };

  const handleSubmit = () => {
    if (!validateFields()) {
      setSnackbar({
        open: true,
        message: "Please fill all the fields",
        severity: "error",
      });
      return;
    }

    if (desc === "Height & Weight") {
      let values: defaultBtnObj[] = [];
      if (activeTab === "Imperial") {
        values.push(heightFeet);
        values.push(heightInches);
      } else if (activeTab === "Metric") {
        values.push(heightCM);
      }
      values.push(weight);
      onSubmit(desc, values);
      setHeightCM(undefined);
      setHeightFeet(undefined);
      setHeightInches(undefined);
      setWeight(undefined);
    } else if (desc === "Temperature") {
      // Safely extract inputs
      const mainInput = temperatureValue?.inputValue?.toString().trim() || "";
      const decimalInput =
        temperatureDecimalValue?.inputValue?.toString().trim() || "";

      // Clean and combine
      let combinedValue;

      if (mainInput && decimalInput) {
        combinedValue = parseFloat(mainInput) + parseFloat(decimalInput);
      } else if (mainInput) {
        combinedValue = mainInput;
      } else if (decimalInput) {
        combinedValue = `0.${decimalInput}`;
      }

      // Determine unit
      const unit = activeTab || "Celsius";

      // Submit only if valid
      if (combinedValue) {
        onSubmit(desc, [
          { inputValue: combinedValue.toString(), unitValue: unit },
        ]);
      }

      // Reset
      setTemperatureValue({ inputValue: "", unitValue: activeTab });
      setTemperatureDecimalValue({ inputValue: "", unitValue: activeTab });
    } else {
      const finalUnit =
        desc === "Blood Pressure"
          ? "mmHg"
          : desc === "Heart Rate"
            ? "BPM"
            : unitValue;
      onSubmit(desc, [{ inputValue, unitValue: finalUnit }]);
      setInputValue("");
      setUnitValue(
        desc === "Blood Pressure" ? "mmHg" : desc === "Heart Rate" ? "BPM" : ""
      );
    }

    handleClose();
  };

  return (
    <div>
      {desc && (
        <button
          onClick={() => {
            const alreadExists = data.some((e: any) => e.description === desc);
            if (desc === "Body Mass Index") {
              onSubmit(desc, [{ inputValue: "", unitValue: "" }]);
              if (alreadExists) {
                handleRemove();
              }
            } else {
              if (alreadExists) {
                handleRemove();
              } else {
                handleOpen();
              }
            }
          }}
          style={{ fontSize: optionFontSize, minHeight: optionBoxHeight, flex: 1 }}
          className={`linear m-1 mr-3 flex w-full items-center justify-center rounded-lg bg-surfaceSecondary p-1 font-bold text-brand-500 transition duration-200 hover:cursor-pointer hover:bg-surfaceSecondary/80 active:bg-surfaceSecondary/70 ${isInData ? "!bg-brand-500 !text-white" : ""
            }`}
        >
          {desc}
        </button>
      )}
      {open && (
        <div className="bg-black/40 fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm">
          <div className="relative relative !w-[40%] rounded-2xl border border-shadowSecondary bg-surfacePrimary p-8 shadow-xl">
            {/* Close Button */}
            <button
              className="absolute right-4 top-3 text-2xl font-bold text-textSecondary hover:text-textPrimary"
              onClick={handleClose}
            >
              &times;
            </button>

            {/* Dialog Content */}
            <div className="wizardBody flex flex-col gap-6">
              <h2
                style={{ fontSize: modalTitleFontSize }}
                className="font-semibold text-textPrimary"
              >
                {desc}
              </h2>

              {/* Height & Weight Tabs */}
              {desc === "Height & Weight" && (
                <div className="flex border-b border-borderPrimary">
                  <button
                    onClick={() => setActiveTab("Imperial")}
                    className={`px-4 py-2 text-sm font-medium ${activeTab === "Imperial"
                      ? "border-b-2 border-brand-500 text-brand-500"
                      : "text-textSecondary hover:text-brand-500/80"
                      }`}
                  >
                    Imperial
                  </button>
                  <button
                    onClick={() => setActiveTab("Metric")}
                    className={`px-4 py-2 text-sm font-medium ${activeTab === "Metric"
                      ? "border-b-2 border-brand-500 text-brand-500"
                      : "text-textSecondary hover:text-brand-500/80"
                      }`}
                  >
                    Metric
                  </button>
                </div>
              )}

              {desc === "Height & Weight" && (
                <div className="">
                  {activeTab === "Imperial" && (
                    <>
                      <div className="mb-2 flex flex-row gap-3">
                        <select
                          style={{ fontSize: modalTextFontSize, width: "49%" }}
                          className="h-12 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
                          value={heightFeet?.inputValue}
                          onChange={(e) =>
                            setHeightFeet({
                              inputValue: e.target.value,
                              unitValue: "Feets",
                            })
                          }
                        >
                          <option
                            className="bg-surfacePrimary text-textPrimary"
                            value=""
                          >
                            Select
                          </option>
                          {Array.from({ length: 6 }, (_, i) => {
                            const val = 3 + i;
                            return (
                              <option
                                className="bg-surfacePrimary text-textPrimary"
                                key={val}
                                value={val}
                              >
                                {val}
                              </option>
                            );
                          })}
                        </select>

                        <div
                          style={{ fontSize: modalTextFontSize }}
                          className="h-12 flex-1 flex items-center justify-center rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 font-medium text-textPrimary"
                        >
                          Feets
                        </div>
                      </div>

                      <div className="mb-2 flex flex-row gap-3">
                        <select
                          style={{ fontSize: modalTextFontSize, width: "49%" }}
                          className="h-12 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
                          value={heightInches?.inputValue}
                          onChange={(e) =>
                            setHeightInches({
                              inputValue: e.target.value,
                              unitValue: "Inches",
                            })
                          }
                        >
                          <option
                            className="bg-surfacePrimary text-textPrimary"
                            value=""
                          >
                            Select
                          </option>
                          {Array.from({ length: 11 }, (_, i) => {
                            const val = 0 + i;
                            return (
                              <option
                                className="bg-surfacePrimary text-textPrimary"
                                key={val}
                                value={val}
                              >
                                {val}
                              </option>
                            );
                          })}
                        </select>

                        <div
                          style={{ fontSize: modalTextFontSize }}
                          className="h-12 flex-1 flex items-center justify-center rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 font-medium text-textPrimary"
                        >
                          Inches
                        </div>
                      </div>

                      <div className="mb-2 flex flex-row gap-3">
                        <input
                          id="label"
                          autoFocus
                          style={{ fontSize: modalTextFontSize }}
                          placeholder={`Enter weight in pounds`}
                          className={`h-12 flex-1 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none placeholder:text-textSecondary focus:border-brand-500 focus:ring-2 focus:ring-brand-200`}
                          type="number"
                          value={weight?.inputValue}
                          onChange={(e) =>
                            setWeight({
                              inputValue: e.target.value,
                              unitValue: "Pounds",
                            })
                          }
                        />
                        <div
                          style={{ fontSize: modalTextFontSize }}
                          className="h-12 flex-1 flex items-center justify-center rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 font-medium text-textPrimary"
                        >
                          Pounds
                        </div>
                      </div>
                    </>
                  )}

                  {activeTab === "Metric" && (
                    <>
                      <div className="mb-2 flex flex-row gap-3">
                        <input
                          id="label"
                          autoFocus
                          style={{ fontSize: modalTextFontSize }}
                          placeholder={`Enter height in centimeters`}
                          className={`h-12 flex-1 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none placeholder:text-textSecondary focus:border-brand-500 focus:ring-2 focus:ring-brand-200`}
                          type="number"
                          value={heightCM?.inputValue}
                          onChange={(e) =>
                            setHeightCM({
                              inputValue: e.target.value,
                              unitValue: "Centimeters",
                            })
                          }
                        />

                        <div
                          style={{ fontSize: modalTextFontSize }}
                          className="h-12 flex-1 flex items-center justify-center rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 font-medium text-textPrimary"
                        >
                          Centimeters
                        </div>
                      </div>

                      <div className="mb-2 flex flex-row gap-3">
                        <input
                          id="label"
                          style={{ fontSize: modalTextFontSize }}
                          placeholder={`Enter weight in kilos`}
                          className={`h-12 flex-1 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none placeholder:text-textSecondary focus:border-brand-500 focus:ring-2 focus:ring-brand-200`}
                          type="number"
                          value={weight?.inputValue}
                          onChange={(e) =>
                            setWeight({
                              inputValue: e.target.value,
                              unitValue: "Kilos",
                            })
                          }
                        />

                        <div
                          style={{ fontSize: modalTextFontSize }}
                          className="h-12 flex-1 flex items-center justify-center rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 font-medium text-textPrimary"
                        >
                          Kilos
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* Temperature Tabs */}
              {desc === "Temperature" && (
                <div className="flex border-b border-borderPrimary">
                  <button
                    onClick={() => {
                      setActiveTab("Celsius");
                      setTemperatureValue({
                        ...temperatureValue,
                        unitValue: "Celsius",
                      });
                      setTemperatureDecimalValue({
                        ...temperatureDecimalValue,
                        unitValue: "Celsius",
                      });
                    }}
                    className={`px-4 py-2 text-sm font-medium ${activeTab === "Celsius"
                      ? "border-b-2 border-brand-500 text-brand-500"
                      : "text-textSecondary hover:text-brand-500/80"
                      }`}
                  >
                    Celsius
                  </button>
                  <button
                    onClick={() => {
                      setActiveTab("Fahrenheit");
                      setTemperatureValue({
                        ...temperatureValue,
                        unitValue: "Fahrenheit",
                      });
                      setTemperatureDecimalValue({
                        ...temperatureDecimalValue,
                        unitValue: "Fahrenheit",
                      });
                    }}
                    className={`px-4 py-2 text-sm font-medium ${activeTab === "Fahrenheit"
                      ? "border-b-2 border-brand-500 text-brand-500"
                      : "text-textSecondary hover:text-brand-500/80"
                      }`}
                  >
                    Fahrenheit
                  </button>
                </div>
              )}
              {desc === "Temperature" && (
                <div className="flex flex-row gap-2">
                  <select
                    style={{ fontSize: modalTextFontSize, width: "20%" }}
                    className="h-12 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
                    value={temperatureValue?.inputValue}
                    onChange={(e) =>
                      setTemperatureValue({
                        ...temperatureValue,
                        inputValue: e.target.value,
                      })
                    }
                  >
                    <option
                      className="bg-surfacePrimary text-textPrimary"
                      value=""
                    >
                      Select
                    </option>
                    {Array.from(
                      { length: activeTab === "Fahrenheit" ? 10 : 6 },
                      (_, i) => {
                        const val =
                          activeTab === "Fahrenheit" ? 96 + i : 36 + i; // generates 36, 37, 38, 39, 40, 41
                        return (
                          <option
                            className="bg-surfacePrimary text-textPrimary"
                            key={val}
                            value={val}
                          >
                            {val}
                          </option>
                        );
                      }
                    )}
                  </select>
                  <select
                    style={{ fontSize: modalTextFontSize, width: "20%" }}
                    className="h-12 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
                    value={temperatureDecimalValue?.inputValue}
                    onChange={(e) =>
                      setTemperatureDecimalValue({
                        ...temperatureDecimalValue,
                        inputValue: e.target.value,
                      })
                    }
                  >
                    <option
                      className="bg-surfacePrimary text-textPrimary"
                      value=""
                    >
                      Select
                    </option>
                    {Array.from({ length: 9 }, (_, i) => {
                      const val = (i + 1) / 10; // generates 0.1, 0.2, ..., 0.9
                      return (
                        <option
                          className="bg-surfacePrimary text-textPrimary"
                          key={val}
                          value={val.toFixed(1)}
                        >
                          {val.toFixed(1)}
                        </option>
                      );
                    })}
                  </select>
                  <div
                    style={{ fontSize: modalTextFontSize, width: "60%" }}
                    className="h-12 flex items-center justify-center rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 font-medium text-textPrimary"
                  >
                    {activeTab}
                  </div>
                </div>
              )}

              <div className="flex flex-row gap-3">
                {desc === "Blood Pressure" && (
                  <>
                    <input
                      id="label"
                      autoFocus
                      style={{ fontSize: modalTextFontSize }}
                      className="h-12 flex-1 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none placeholder:text-textSecondary focus:border-brand-500 focus:ring-2 focus:ring-brand-200"
                      type="text"
                      value={inputValue}
                      onChange={handleChange}
                      placeholder="e.g. 55/65"
                    />
                    <div
                      style={{ fontSize: modalTextFontSize }}
                      className="h-12 flex-1 flex items-center justify-center rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 font-medium text-textPrimary"
                    >
                      mmHg
                    </div>
                  </>
                )}

                {desc === "Heart Rate" && (
                  <>
                    <input
                      id="label"
                      autoFocus
                      style={{ fontSize: modalTextFontSize }}
                      placeholder={`Enter ${desc}`}
                      className={`h-12 flex-1 rounded-xl border border-borderPrimary bg-surfacePrimary px-4 text-textPrimary outline-none placeholder:text-textSecondary focus:border-brand-500 focus:ring-2 focus:ring-brand-200`}
                      type="number"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                    />
                    <div
                      style={{ fontSize: modalTextFontSize }}
                      className="h-12 flex-1 flex items-center justify-center rounded-xl border border-borderPrimary bg-surfaceSecondary px-4 font-medium text-textPrimary"
                    >
                      BPM
                    </div>
                  </>
                )}
              </div>

              {/* Save Button */}
              <div className="mt-4 flex justify-end">
                <button
                  onClick={handleSubmit}
                  className="rounded-lg bg-brand-500 px-6 py-3 text-sm font-semibold text-white transition duration-200 hover:bg-brand-500/80 active:bg-brand-500/70"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      <AppSnackbar
        isOpen={snackbar.open}
        title={snackbar.message}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        severity={snackbar.severity}
      />
    </div>
  );
};
export default DefaultBtnModel;
